import { countryCodes } from '../translations/countryCodes';

const countryOptions = countryCodes.map(c => ({
  option: c.code,
  label: c.en,
  labelId: `ListingField.country.option.${c.code}`,
}));

/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Sharetribe CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'dialects',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'LB', label: 'Lebanese', labelId: 'ListingField.dialects.option.LB' },
      { option: 'SY', label: 'Syrian', labelId: 'ListingField.dialects.option.SY' },
      { option: 'JO', label: 'Jordanian', labelId: 'ListingField.dialects.option.JO' },
      { option: 'PS', label: 'Palestinian', labelId: 'ListingField.dialects.option.PS' },
      { option: 'EG', label: 'Egyptian', labelId: 'ListingField.dialects.option.EG' },
      { option: 'AE', label: 'Emirati', labelId: 'ListingField.dialects.option.AE' },
      { option: 'SA', label: 'Saudi', labelId: 'ListingField.dialects.option.SA' },
      { option: 'KW', label: 'Kuwaiti', labelId: 'ListingField.dialects.option.KW' },
      { option: 'QA', label: 'Qatari', labelId: 'ListingField.dialects.option.QA' },
      { option: 'BH', label: 'Bahraini', labelId: 'ListingField.dialects.option.BH' },
      { option: 'OM', label: 'Omani', labelId: 'ListingField.dialects.option.OM' },
      { option: 'YE', label: 'Yemeni', labelId: 'ListingField.dialects.option.YE' },
      { option: 'IQ', label: 'Iraqi', labelId: 'ListingField.dialects.option.IQ' },
      { option: 'SD', label: 'Sudanese', labelId: 'ListingField.dialects.option.SD' },
      { option: 'LY', label: 'Libyan', labelId: 'ListingField.dialects.option.LY' },
      { option: 'TN', label: 'Tunisian', labelId: 'ListingField.dialects.option.TN' },
      { option: 'DZ', label: 'Algerian', labelId: 'ListingField.dialects.option.DZ' },
      { option: 'MA', label: 'Moroccan', labelId: 'ListingField.dialects.option.MA' },
      { option: 'msa', label: 'Modern Standard Arabic', labelId: 'ListingField.dialects.option.msa' },
      // Aliases for backward compatibility mapped to country names
      { option: 'levantine', label: 'Lebanese', labelId: 'ListingField.dialects.option.levantine' },
      { option: 'gulf', label: 'Emirati', labelId: 'ListingField.dialects.option.gulf' },
      { option: 'egyptian', label: 'Egyptian', labelId: 'ListingField.dialects.option.egyptian' },
      { option: 'moroccan', label: 'Moroccan', labelId: 'ListingField.dialects.option.moroccan' },
      { option: 'palestinian', label: 'Palestinian', labelId: 'ListingField.dialects.option.palestinian' },
      { option: 'syrian', label: 'Syrian', labelId: 'ListingField.dialects.option.syrian' },
      { option: 'iraqi', label: 'Iraqi', labelId: 'ListingField.dialects.option.iraqi' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      searchMode: 'has_any',
      label: 'Arabic / Dialect',
      labelId: 'ListingField.dialects.label',
      group: 'primary',
    },
    showConfig: {
      label: 'Dialects',
      isDetail: true,
    },
    saveConfig: {
      label: 'Dialects',
      placeholderMessage: 'Select one or more dialects…',
      isRequired: true,
    },
  },
  {
    key: 'teaching_categories',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'arabic_for_kids', label: 'Arabic for Kids', labelId: 'ListingField.teachingCategories.option.arabic_for_kids' },
      { option: 'modern_standard_arabic_msa', label: 'Modern Standard Arabic', labelId: 'ListingField.teachingCategories.option.modern_standard_arabic_msa' },
      { option: 'arabic_for_school', label: 'Arabic for School', labelId: 'ListingField.teachingCategories.option.arabic_for_school' },
      { option: 'arabic_for_work', label: 'Arabic for Work', labelId: 'ListingField.teachingCategories.option.arabic_for_work' },
      { option: 'stories_and_songs', label: 'Stories & Songs', labelId: 'ListingField.teachingCategories.option.stories_and_songs' },
      { option: 'quranic_arabic', label: 'Quranic Arabic', labelId: 'ListingField.teachingCategories.option.quranic_arabic' },
      { option: 'arabic_from_scratch', label: 'Arabic from Scratch', labelId: 'ListingField.teachingCategories.option.arabic_from_scratch' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      searchMode: 'has_any',
      label: 'What they teach',
      labelId: 'ListingField.teachingCategories.label',
      group: 'primary',
    },
    showConfig: {
      label: 'What they teach',
      isDetail: true,
    },
    saveConfig: {
      label: 'What they teach',
      placeholderMessage: 'Select what you teach…',
      isRequired: false,
    },
  },
  {
    key: 'ageGroup',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'kids', label: 'Kids 5–12', labelId: 'ListingField.ageGroup.option.kids' },
      { option: 'teenagers', label: 'Teens 13–17', labelId: 'ListingField.ageGroup.option.teenagers' },
      { option: 'adults', label: 'Adults 18 and over', labelId: 'ListingField.ageGroup.option.adults' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      searchMode: 'has_any',
      label: 'Age Group',
      labelId: 'ListingField.ageGroup.label',
      group: 'primary',
    },
    showConfig: {
      label: 'Age Group',
      isDetail: true,
    },
    saveConfig: {
      label: 'Age Group',
      placeholderMessage: 'Select an age group…',
      isRequired: false,
    },
  },
  {
    key: 'teachingLanguages',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'arabic', label: 'Arabic', labelId: 'ListingField.teachingLanguages.option.arabic' },
      { option: 'english', label: 'English', labelId: 'ListingField.teachingLanguages.option.english' },
      { option: 'french', label: 'French', labelId: 'ListingField.teachingLanguages.option.french' },
      { option: 'spanish', label: 'Spanish', labelId: 'ListingField.teachingLanguages.option.spanish' },
      { option: 'german', label: 'German', labelId: 'ListingField.teachingLanguages.option.german' },
      { option: 'turkish', label: 'Turkish', labelId: 'ListingField.teachingLanguages.option.turkish' },
      { option: 'urdu', label: 'Urdu', labelId: 'ListingField.teachingLanguages.option.urdu' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      searchMode: 'has_any',
      label: 'Language of Instruction',
      labelId: 'ListingField.teachingLanguages.label',
      group: 'primary',
    },
    showConfig: {
      label: 'Language of Instruction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Language of Instruction',
      placeholderMessage: 'Select languages you can teach in…',
      isRequired: false,
    },
  },
  {
    key: 'countryOfOrigin',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: countryOptions,
    showConfig: {
      label: 'Country of Origin',
      isDetail: false,
    },
    saveConfig: {
      label: 'Country of Origin',
      placeholderMessage: 'Select country…',
      isRequired: false,
    },
  },
  {
    key: 'country',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: countryOptions,
    showConfig: {
      label: 'Country of Residence',
      isDetail: false,
    },
    saveConfig: {
      label: 'Country of Residence',
      placeholderMessage: 'Select country…',
      isRequired: false,
    },
  },
  {
    key: 'levels',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'beginner', label: 'Beginner', labelId: 'ListingField.levels.option.beginner' },
      { option: 'intermediate', label: 'Intermediate', labelId: 'ListingField.levels.option.intermediate' },
      { option: 'advanced', label: 'Advanced', labelId: 'ListingField.levels.option.advanced' },
      { option: 'business', label: 'Business', labelId: 'ListingField.levels.option.business' },
      { option: 'exam_prep', label: 'Exam Prep', labelId: 'ListingField.levels.option.exam_prep' },
    ],
    showConfig: {
      label: 'Language Level',
      isDetail: false,
    },
    saveConfig: {
      label: 'Language Level',
      placeholderMessage: 'Select a level…',
      isRequired: false,
    },
  },
  {
    key: 'teachingExperience',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: '1_3', label: '1–3 years', labelId: 'ListingField.teachingExperience.option.1_3' },
      { option: '3_6', label: '3–6 years', labelId: 'ListingField.teachingExperience.option.3_6' },
      { option: '6_plus', label: '6 years and over', labelId: 'ListingField.teachingExperience.option.6_plus' },
    ],
    showConfig: {
      label: 'Teaching experience',
      isDetail: true,
    },
    saveConfig: {
      label: 'Teaching experience',
      placeholderMessage: 'Select teaching experience…',
      isRequired: false,
    },
  },
  {
    key: 'specializations',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'conversational_arabic', label: 'Conversational Arabic', labelId: 'ListingField.specializations.option.conversational_arabic' },
      { option: 'quranic_arabic', label: 'Quranic Arabic', labelId: 'ListingField.specializations.option.quranic_arabic' },
      { option: 'business_arabic', label: 'Business Arabic', labelId: 'ListingField.specializations.option.business_arabic' },
      { option: 'arabic_from_scratch', label: 'Arabic from Scratch (Beginners)', labelId: 'ListingField.specializations.option.arabic_from_scratch' },
    ],
    showConfig: {
      label: 'Specializations',
      isDetail: true,
    },
    saveConfig: {
      label: 'Specializations',
      placeholderMessage: 'Select one or more…',
      isRequired: false,
    },
  },
  {
    key: 'teachFromZero',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'true', label: 'Yes - I teach from scratch', labelId: 'ListingField.teachFromZero.option.yes' },
      { option: 'false', label: 'No', labelId: 'ListingField.teachFromZero.option.no' },
    ],
    showConfig: {
      label: 'Teaches from Scratch',
      isDetail: true,
    },
    saveConfig: {
      label: 'Can you teach absolute beginners with no prior Arabic background (from scratch)?',
      placeholderMessage: 'Select option…',
      isRequired: false,
    },
  },
  {
    key: 'lessonTypes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'structured', label: 'Structured', labelId: 'ListingField.lessonTypes.option.structured' },
      { option: 'conversational', label: 'Conversational', labelId: 'ListingField.lessonTypes.option.conversational' },
      { option: 'both', label: 'Both', labelId: 'ListingField.lessonTypes.option.both' },
    ],
    showConfig: {
      label: 'Lesson types offered',
      isDetail: true,
    },
    saveConfig: {
      label: 'Lesson types offered',
      placeholderMessage: 'Select lesson types…',
      isRequired: true,
    },
  },
//   {
//     key: 'tutorMinRating',
//     scope: 'public',
//     schemaType: 'enum',
//     // Options encode minimum rating x10; SearchPage.shared maps to "min,50" for API.
//     enumOptions: [
//       { option: '10', label: '1 star & up', labelId: 'ListingField.tutorMinRating.option.1starandabove' },
//       { option: '20', label: '2 stars & up', labelId: 'ListingField.tutorMinRating.option.2starsandabove' },
//       { option: '30', label: '3 stars & up', labelId: 'ListingField.tutorMinRating.option.3starsandabove' },
//       { option: '40', label: '4 stars & up', labelId: 'ListingField.tutorMinRating.option.4starsandabove' },
//       { option: '50', label: '5 stars', labelId: 'ListingField.tutorMinRating.option.5stars' },
//     ],
//     filterConfig: {
//       indexForSearch: true,
//       filterType: 'SelectSingleFilter',
//       label: 'Rating',
//       labelId: 'ListingField.tutorMinRating.label',
//       group: 'primary',
//     },
//     showConfig: {
//       label: 'Rating',
//       isDetail: true,
//     },
//   },
  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - priceVariations      This is relevant only to listings using default-booking process.
 *   - enabled:             If set to true, price variations are enabled.
 *                          Default: false.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  {
    listingType: 'daily-booking',
    label: 'Daily booking',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    availabilityType: 'oneSeat',
    defaultListingFields: {
      location: true,
      payoutDetails: true,
    },
  },
  {
    listingType: 'blogs',
    label: 'Blogs',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
      location: false,
      payoutDetails: false,
    },
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
